import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, StatusDot, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import TierChip from '../shared/TierChip';
import DiamondAmount from '../shared/DiamondAmount';
import { formatPct } from '../shared/format';

/**
 * Module 4 — Creator list row (mobile + desktop).
 *
 * Mobile = card-style row, vertical info stacked.
 * Desktop = table-style row (passes asTable=true).
 */
const ALERT_COLOR = {
  none: 'green',
  at_risk: 'amber',
  critical: 'red',
};

const CreatorListRow = ({ creator, onClick, asTable = false }) => {
  const { t } = useTranslation();
  if (!creator) return null;

  const alert = creator.alert_status || 'none';
  const alertColor = ALERT_COLOR[alert] || 'green';
  const handle = creator.tiktok_username
    ? `@${creator.tiktok_username}`
    : creator.display_name || creator.tiktok_id || '—';

  if (asTable) {
    return (
      <tr
        onClick={onClick}
        style={{
          cursor: onClick ? 'pointer' : 'default',
          borderBottom: `1px solid ${TCN.border}`,
        }}
        className="hover:bg-tcn-surface2"
      >
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <Avatar name={creator.display_name || handle} size={32} />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: TCN.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {handle}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: TCN.textMute,
                }}
              >
                {creator.display_name || ''}
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <TierChip tier={creator.tier_current} />
        </td>
        <td
          style={{
            padding: '12px 16px',
            verticalAlign: 'middle',
            fontFamily: TCN.fontMono,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
          }}
        >
          {/* diamonds_mtd_enc — encrypted, can't display in clear */}
          <DiamondAmount value={null} encrypted />
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <StatusDot color={alertColor} size={8} />
            <span style={{ fontSize: 12, color: TCN.textDim }}>
              {t(`tcn.m4.alert.${alert}`, alert.replace('_', ' '))}
            </span>
          </span>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          {creator.priority_category ? (
            <Chip
              size="sm"
              color={
                creator.priority_category === 'high_risk'
                  ? 'red'
                  : creator.priority_category === 'high_upside'
                  ? 'green'
                  : creator.priority_category === 'balanced'
                  ? 'cyan'
                  : 'amber'
              }
            >
              {t(`tcn.m4.priority.${creator.priority_category}`, creator.priority_category)}
            </Chip>
          ) : null}
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle', fontSize: 12, color: TCN.textDim }}>
          {creator.activeness_level ? `L${creator.activeness_level}` : '—'}
        </td>
        <td
          style={{
            padding: '12px 16px',
            verticalAlign: 'middle',
            fontFamily: TCN.fontMono,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 12,
            color: TCN.textDim,
          }}
        >
          {creator.rank_up_eligibility_7d15h
            ? t('tcn.m4.rankUpYes', 'Yes')
            : t('tcn.m4.rankUpNo', 'No')}
        </td>
      </tr>
    );
  }

  // Mobile card row
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderBottom: `1px solid ${TCN.border}`,
        cursor: onClick ? 'pointer' : 'default',
        background: TCN.surface1,
      }}
    >
      <Avatar name={creator.display_name || handle} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: TCN.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
          >
            {handle}
          </span>
          <TierChip tier={creator.tier_current} />
          <StatusDot color={alertColor} size={8} />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            color: TCN.textDim,
          }}
        >
          <span>
            {/* diamonds_mtd_enc encrypted — placeholder */}
            <DiamondAmount value={null} encrypted />
          </span>
          {creator.activeness_level >= 1 && (
            <span>L{creator.activeness_level}</span>
          )}
          {typeof creator.diamonds_mtd_enc !== 'string' && creator.alert_status && (
            <span style={{ color: TCN.textMute }}>
              {t(`tcn.m4.alert.${creator.alert_status}`, creator.alert_status)}
            </span>
          )}
          {creator.rank_up_eligibility_7d15h && (
            <Chip color="cyan" size="sm">
              {t('tcn.m4.rankUpEligible', 'Rank-up eligible')}
            </Chip>
          )}
        </div>
      </div>
      <div style={{ flexShrink: 0, color: TCN.textMute, display: 'flex', alignItems: 'center' }}>
        <Icon name="chevron" size={16} />
      </div>
    </div>
  );
};

export default CreatorListRow;
